import {blobToBase64} from '@legit-health/ui';
import useApiMutation from '@/shared/hooks/useApiMutation';
import type {AihsBodySiteCode} from '../regions';

/**
 * Bounding box returned by the AIHS4 severity assessment endpoint, in the
 * original image coordinate system.
 */
export interface AihsDetectionBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface AihsDetection {
  confidence: number;
  code: {
    text: 'Nodule' | 'Abscess' | 'Draining tunnel' | string;
    coding: unknown;
  };
  box: AihsDetectionBox;
}

export interface AihsMediaValidity {
  isValid: boolean;
  quality: {
    acceptable: boolean;
    score: number;
    interpretation: string;
  };
  domain: {
    isDermatological: boolean;
    additionalData?: Record<string, unknown>;
  };
  modality: {
    value: string;
    additionalData?: Record<string, unknown>;
  };
}

export interface AihsScore {
  value: number;
  interpretation: {
    category: string;
    intensity: number;
  };
  globalScoreContribution: number | null;
}

export interface AihsSeverityAssessmentData {
  resourceType: 'DiagnosticReport';
  status: 'final';
  mediaValidity: AihsMediaValidity | null;
  patientEvolution: {
    aihs4: {
      scoringSystemCode: 'aihs4';
      score: AihsScore;
      item: unknown | null;
      media: {
        attachment: unknown | null;
        detection: AihsDetection[];
      };
    };
  };
  analysisDuration: number;
  issued: string;
}

/**
 * Wraps the `POST /severity-assessment/aihs4` endpoint. The caller passes the
 * original captured file (a Blob) plus the target body site code; the hook
 * handles base64 encoding and returns the typed response.
 */
export default function useAihs4SeverityAssessment() {
  const {mutateAsync, isPending, reset} = useApiMutation<AihsSeverityAssessmentData>();

  async function sendRequest({file, bodySiteCode}: {file: Blob; bodySiteCode: AihsBodySiteCode}) {
    const base64Image = await blobToBase64(file);
    const response = await mutateAsync({
      path: '/severity-assessment/aihs4',
      method: 'POST',
      body: {
        image: base64Image,
        bodySiteCode
      }
    });
    return response.data;
  }

  return {sendRequest, isPending, reset};
}
